import { t } from 'i18next';
import { dayjs } from 'lib/dayjs';

export type FormatDataToStringProps<T> = {
  columns: { key: string; label: string }[];
  data: T[];
  dateFormat: string;
  isModec: boolean;
};
export const CSV_DIVIDER = ';';
export function capitalize(string: string) {
  return string.length > 3 ? string[0].toUpperCase() + string.slice(1).toLowerCase() : string;
}

export const formatCSVValue = (value?: string) => {
  const valueWithSeparatedWords = value?.split(/[_ ]/).map(capitalize).join(' ');
  const sanitizedValue = valueWithSeparatedWords?.replace(/[;,]|[\n]/g, '.');
  return sanitizedValue?.replace(/[-]|[\n]/g, ' ') ?? '';
};

const utf8Bom = '\uFEFF'; // BOM UTF-8
const FIELDS_TO_FORMAT_DATE = [
  'closed_at',
  'alarm_datetime_start',
  'alarm_datetime_end',
  'created_at',
  'last_update_date',
  'deviation_start',
  'deviation_end',
  'anomaly_start',
  'anomaly_end',
  'updated_at',
  'start_date',
  'end_date',
];

const formatColumnValue = <T extends { asset: { name: string; id: string } }>({
  key,
  dateFormat,
  row,
  isModec,
}: Omit<FormatDataToStringProps<T>, 'data' | 'columns'> & { row: T; key: string }): string => {
  const value = row[key as keyof T] ?? '';

  if (FIELDS_TO_FORMAT_DATE.includes(key)) {
    return value
      ? dayjs(value as string, ['YYYY-MM-DDTHH:mm:ss+00:00', 'YYYY-MM-DD']).format(dateFormat)
      : ``;
  }

  if (key === 'asset.name') {
    return row.asset.name;
  }
  if (key.includes('.')) {
    const [parentKey, childKey, grandChildKey] = key.split('.');

    const parentValue = row[parentKey as keyof T] as Record<string, string>;

    if (Array.isArray(parentValue)) {
      return parentValue
        .map((v) =>
          formatColumnValue({
            key: grandChildKey ? `${childKey}.${grandChildKey}` : childKey,
            row: v,
            dateFormat,
            isModec,
          }),
        )
        .join(', ');
    }
    if (typeof parentValue === 'object' && parentValue !== null) {
      return formatCSVValue(parentValue[childKey]?.toString());
    }
  }

  if (key === 'severity') {
    return t(`event_page:severity.${value}`, { defaultValue: formatCSVValue(value.toString()) });
  }
  if (key === 'condition_evaluation') {
    return formatCSVValue(
      t(`components:ConditionDeviation.options.${key}${isModec ? '.modec' : ''}.${value}`, {
        defaultValue: value,
      }),
    );
  }
  return formatCSVValue(value.toString());
};
const getRowString = <T extends { asset: { name: string; id: string } }>({
  columns,
  row,
  dateFormat,
  isModec,
}: Omit<FormatDataToStringProps<T>, 'data'> & { row: T }) =>
  columns.reduce(
    (acc, { key }) => acc + formatColumnValue({ row, key, dateFormat, isModec }) + CSV_DIVIDER,
    '',
  );

export const formatDataToStringCsv = <
  T extends { children?: Omit<T, 'children'>[]; asset: { name: string; id: string } },
>({
  columns,
  data,
  dateFormat,
  isModec,
}: FormatDataToStringProps<T>): string => {
  const csvHeaders = columns.map(({ label }) => label).join(';');

  const csvContent = data.map((row) => {
    if (row.children) {
      return row.children
        .map((child: Omit<T, 'children'>) =>
          getRowString<Omit<T, 'children'>>({ row: child, columns, dateFormat, isModec }),
        )
        .join('\n');
    }
    return getRowString<Omit<T, 'children'>>({ row, columns, dateFormat, isModec });
  });

  return `${utf8Bom}${csvHeaders}\n${csvContent.join('\n')}`;
};
